import { useState } from 'react';
import { Card, Button, Input } from '@atoms/index';
import { ConfirmModal } from './ConfirmModal';
import { cn } from '@/utils/cn';

export type RejectionReason =
  | 'TOO_BUSY'
  | 'OUT_OF_STOCK'
  | 'RESTAURANT_CLOSED'
  | 'OUT_OF_SERVICE_AREA'
  | 'BELOW_MINIMUM'
  | 'OTHER';

interface RejectOrderModalProps {
  isOpen: boolean;
  orderNumber: string;
  onConfirm: (reason: RejectionReason, message?: string) => Promise<void>;
  onClose: () => void;
}

const rejectionReasons: Array<{ value: RejectionReason; label: string; description: string }> = [
  {
    value: 'TOO_BUSY',
    label: 'Çok Meşgul',
    description: 'Restoran şu anda çok meşgul, yeni sipariş kabul edemiyor',
  },
  {
    value: 'OUT_OF_STOCK',
    label: 'Stokta Yok',
    description: 'Siparişteki ürünlerden biri veya birkaçı stokta yok',
  },
  {
    value: 'RESTAURANT_CLOSED',
    label: 'Restoran Kapalı',
    description: 'Restoran şu anda kapalı',
  },
  {
    value: 'OUT_OF_SERVICE_AREA',
    label: 'Servis Bölgesi Dışı',
    description: 'Teslimat adresi servis bölgesi dışında',
  },
  {
    value: 'BELOW_MINIMUM',
    label: 'Minimum Tutar Altı',
    description: 'Sipariş tutarı minimum sipariş tutarının altında',
  },
  {
    value: 'OTHER',
    label: 'Diğer',
    description: 'Diğer bir sebep (açıklama gereklidir)',
  },
];

export function RejectOrderModal({
  isOpen,
  orderNumber,
  onConfirm,
  onClose,
}: RejectOrderModalProps) {
  const [selectedReason, setSelectedReason] = useState<RejectionReason | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!selectedReason) return;

    if (selectedReason === 'OTHER' && !customMessage.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(selectedReason, selectedReason === 'OTHER' ? customMessage : undefined);
      setSelectedReason(null);
      setCustomMessage('');
    } catch (error) {
      console.error('Failed to reject order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = selectedReason && (selectedReason !== 'OTHER' || customMessage.trim().length > 0);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <button
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Modal kapat"
      />
      <Card className="relative z-[10000] w-full max-w-lg p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Siparişi Reddet
          </h3>
          <p className="text-gray-600 mb-6">
            Sipariş #{orderNumber} için red sebebi seçin
          </p>

          <div className="space-y-3 mb-6">
            {rejectionReasons.map((reason) => (
              <button
                key={reason.value}
                onClick={() => setSelectedReason(reason.value)}
                className={cn(
                  'w-full text-left p-4 border-2 rounded-lg transition-colors',
                  selectedReason === reason.value
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300',
                )}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      'w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center',
                      selectedReason === reason.value
                        ? 'border-primary bg-primary'
                        : 'border-gray-300',
                    )}
                  >
                    {selectedReason === reason.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{reason.label}</p>
                    <p className="text-sm text-gray-600 mt-1">{reason.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {selectedReason === 'OTHER' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Red Sebebi Açıklaması *
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Red sebebini açıklayın..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {customMessage.length}/500 karakter
              </p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Vazgeç
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleConfirm}
              disabled={!canSubmit || isSubmitting}
              isLoading={isSubmitting}
            >
              Siparişi Reddet
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
